// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@hardwoods/App/Components/Notify/Notify";
import FancyScroll from "@hardwoods/XDL/components/common/FancyScroll";
import color from "@hardwoods/XDL/Utilities/color";
import { styledScrollbar } from "@hardwoods/XDL/Utilities/SharedStyles";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import getLocalizedTime from "@insite/client-framework/Common/Utilities/getLocalizedTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import updateMessageIsRead from "@insite/client-framework/Store/Data/Messages/Handlers/UpdateMessageIsRead";
import { getMessagesDataView } from "@insite/client-framework/Store/Data/Messages/MessagesSelector";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import Accordion from "@insite/mobius/Accordion";
import { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import {
    AccordionSectionHeader,
    AccordionSectionPresentationProps,
    ManagedAccordionSection,
} from "@insite/mobius/AccordionSection";
import AccordionSectionPanel from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const messagesParameter = {};

const mapStateToProps = (state: ApplicationState) => ({
    messagesDataView: getMessagesDataView(state, messagesParameter),
    language: state.context.session.language,
});

const mapDispatchToProps = {
    updateMessageIsRead,
    loadMessages,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountMessagesStyles {
    centeringWrapper?: InjectableCss;
    messagesSection?: InjectableCss;
    messagesAccordion?: AccordionPresentationProps;
    messagesAccordionSection?: AccordionSectionPresentationProps;
    messagesGridContainer?: GridContainerProps;
    messageFilterGridItem?: GridItemProps;
    messageFilter?: MessageFilterStyles;
    messageCardGridItem?: GridItemProps;
    messageCardsWrapperGridItem?: GridItemProps;
    messageCardsGridContainer?: GridContainerProps;
    messageCard?: MessageCardStyles;
    messagesTitle?: TypographyProps;
    noMessagesGridItem?: GridItemProps;
    noMessagesText?: TypographyPresentationProps;
}

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

interface MessageCardStyles {
    article?: InjectableCss;
    gridContainer?: GridContainerProps;
    dateTimeGridItem?: GridItemProps;
    dateTimeText?: TypographyProps;
    subjectGridItem?: GridItemProps;
    subjectText?: TypographyProps;
    bodyGridItem?: GridItemProps;
    bodyText?: TypographyProps;
    actionButtonGridItem?: GridItemProps;
    actionButton?: LinkPresentationProps;
    bottomBorderGridItem?: GridItemProps;
    bottomBorder?: InjectableCss;
    // XNG-Changes
    accordionSection?: AccordionSectionPresentationProps;
    titleSection?: InjectableCss;
    messagesAccordionTitle?: InjectableCss;
    overflowMenu?: OverflowMenuProps;
}

// XNG-Changes: update style
export const accountMessagesStyles: AccountMessagesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100px;
        `,
    },
    messagesSection: {
        css: css`
            dt[data-test-selector="sectionHeader"] button {
                position: relative;
            }
        `,
    },
    messagesAccordion: {
        css: css`
            width: 100%;
        `,
    },
    messagesGridContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    messageFilterGridItem: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-bottom: 20px;
        `,
    },
    messagesTitle: {
        variant: "h2",
        transform: "capitalize",
        css: css`
            margin-right: 16px;
            margin-bottom: 10px;
            > span {
                margin-left: 5px;
            }
        `,
    },
    messageCardsWrapperGridItem: {
        css: css`
            width: 100%;
            max-height: 289px;
            overflow: auto;
            ${styledScrollbar()}
            &&& {
                margin-bottom: 20px;
            }
        `,
    },
    messageCardsGridContainer: {
        gap: 0,
        css: css`
            > div > ${GridItemStyle} {
                &:nth-child(even) {
                    ${AccordionSectionHeader} {
                        background: ${color("common.accent")};
                    }
                }
                ${AccordionSectionHeader} {
                    padding-left: 20px;
                    padding-right: 20px;
                }
            }
        `,
    },
    messageFilter: {
        radioGroup: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                flex-wrap: wrap;

                div[class^="RadioStyle"] {
                    margin-top: 10px;

                    label {
                        margin-right: 30px;
                    }
                }
            `,
        },
        radioButton: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    messageCardGridItem: { width: 12 },
    messagesAccordionSection: {
        headerProps: {
            css: css`
                ${getProp("theme.accordion.sectionDefaultProps.headerProps.css")};
                ${/* sc-selector */ AccordionSectionPanel} + & button {
                    border-top: 0;
                    padding: 0;
                }
                button {
                    &,
                    &:focus {
                        border-top: 0;
                        padding: 0;
                    }
                }
            `,
        },
    },
    messageCard: {
        gridContainer: {
            gap: 5,
            css: css`
                margin-left: 15px;
            `,
        },
        article: {
            css: css`
                width: 100%;
            `,
        },
        dateTimeGridItem: { width: 12 },
        dateTimeText: {
            weight: "bold",
        },
        subjectGridItem: {
            width: [10, 8, 9, 9, 9],
            css: css`
                display: block;
                margin-left: 12px;
            `,
        },
        bodyGridItem: { width: 12 },
        actionButtonGridItem: {
            width: [2, 4, 3, 3, 3],
            css: css`
                justify-content: flex-end;
                border: 0;
                align-items: center;
            `,
        },
        subjectText: {
            as: "p",
            css: css`
                font-size: 14px;
                padding-top: 5px;
            `,
        },
        actionButton: {
            color: "common.ui12",
            hoverStyle: {
                color: color("common.ui14")(),
            },
            css: css`
                &&& {
                    background: transparent;
                    border: 0;
                    padding: 0 10px;
                }
            `,
        },
        bottomBorderGridItem: { width: 12 },
        bottomBorder: {
            css: css`
                height: 1px;
                width: 100%;
                background: ${getColor("common.accent")};
            `,
        },
        accordionSection: {
            headerProps: {
                css: css`
                    > button {
                        &,
                        &:focus {
                            padding: 10px 0 10px 20px;
                            background: transparent;
                            border: 0;
                        }
                    }
                `,
            },
            panelProps: {
                css: css`
                    padding: 5px 0 0 20px;
                    border: 0;
                `,
            },
            toggleTransition: {
                positionerCss: css`
                    position: absolute;
                    left: 0;
                    padding-left: 0;
                `,
            },
            toggleIconProps: {
                size: 12,
            },
            expandIconProps: {
                src: "Plus",
                color: "primary.dark",
            },
            collapseIconProps: {
                src: "Minus",
                color: "danger",
            },
        },
        titleSection: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        messagesAccordionTitle: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                width: 100%;
            `,
        },
        overflowMenu: {
            buttonProps: {
                css: css`
                    &&& {
                        ${getProp("theme.overflowMenu.defaultProps.buttonProps.css")}
                        padding: 9px;
                        background: transparent;
                        border-color: transparent;
                        z-index: 1255;
                    }
                `,
            },
        },
    },
};

function filterMessagesBasedOnFilterType(messages: MessageModel[], messagesFilterType: string): MessageModel[] {
    return messages.filter(message => {
        if (messagesFilterType === "All") {
            return true;
        }

        if (messagesFilterType === "Unread") {
            return !message.isRead;
        }

        return message.isRead;
    });
}

const styles = accountMessagesStyles;
const StyledSection = getStyledWrapper("section");
const StyledArticle = getStyledWrapper("article");

interface State {
    messagesFilterType: string;
}

class AccountMessages extends React.Component<Props, State> {
    refFancyScroll = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            messagesFilterType: "All",
        };
    }

    componentDidMount() {
        if (!this.props.messagesDataView.value) {
            this.props.loadMessages(messagesParameter);
        }
    }

    render() {
        const title = "Messages";
        let countUnreadMessages;
        const { messagesDataView, updateMessageIsRead, language } = this.props;
        const { messagesFilterType } = this.state;
        const messages = messagesDataView.value ? messagesDataView.value : [];

        if (messagesDataView.value) {
            countUnreadMessages = messages.filter(message => !message.isRead).length;
        }

        const filteredMessages = filterMessagesBasedOnFilterType(messages, messagesFilterType);
        const componentStyles: MessageFilterStyles = styles.messageFilter || {};

        return (
            <StyledSection {...styles.messagesSection} data-test-selector="myAccountMessagesSection">
                <StyledWrapper {...styles.messagesGridContainer}>
                    <StyledWrapper {...styles.messageFilterGridItem}>
                        <Typography {...styles.messagesTitle}>
                            {title}
                            {countUnreadMessages ? (
                                <Typography color="common.secondary2">{`(${countUnreadMessages})`}</Typography>
                            ) : (
                                ""
                            )}
                        </Typography>
                        {messagesDataView.value && (
                            <RadioGroup
                                as="legend"
                                {...componentStyles.radioGroup}
                                value={messagesFilterType}
                                onChangeHandler={(event: any) =>
                                    this.setState({ messagesFilterType: event.target.value })
                                }
                            >
                                <Radio {...componentStyles.radioButton} key="All">
                                    {translate("All")}
                                </Radio>
                                <Radio {...componentStyles.radioButton} key="Unread">
                                    {translate("Unread")}
                                </Radio>
                                <Radio {...componentStyles.radioButton} key="Read">
                                    {translate("Read")}
                                </Radio>
                            </RadioGroup>
                        )}
                    </StyledWrapper>
                    <FancyScroll
                        refElement={this.refFancyScroll}
                        showUp={true}
                        updatedConditions={[...(filteredMessages ?? [])]}
                    >
                        <StyledWrapper {...styles.messageCardsWrapperGridItem} ref={this.refFancyScroll}>
                            {filteredMessages && (
                                <GridContainer {...styles.messageCardsGridContainer}>
                                    {filteredMessages.map(message => (
                                        <GridItem {...styles.messageCardGridItem} key={`${message.id}`}>
                                            <MessageCard
                                                message={message}
                                                updateMessageIsRead={updateMessageIsRead}
                                                language={language}
                                            />
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            )}
                            {filteredMessages.length === 0 && (
                                <StyledWrapper {...styles.noMessagesGridItem}>
                                    <Notify messageType="info" body={translate("No messages")} />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </FancyScroll>
                </StyledWrapper>
                {messagesDataView.isLoading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner />
                    </StyledWrapper>
                )}
            </StyledSection>
        );
    }
}

type MessageCardProps = {
    message: MessageModel;
} & Pick<Props, "updateMessageIsRead" | "language">;

const MessageCard = ({ message, language, updateMessageIsRead }: MessageCardProps) => {
    const handleClickMessageReadStatus = () =>
        updateMessageIsRead({
            message,
            isRead: !message.isRead,
        });

    const dateDisplay = getLocalizedDateTime({
        dateTime: message.dateToDisplay,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const timeDisplay = getLocalizedTime({
        dateTime: message.dateToDisplay,
        language,
        options: {
            hour: "numeric",
            minute: "numeric",
        },
    });
    const componentStyles: MessageCardStyles = styles.messageCard || {};

    // XNG-Changes: update structure
    return (
        <StyledArticle {...styles.messageCard?.article}>
            <Accordion headingLevel={2}>
                <ManagedAccordionSection
                    title={
                        <GridContainer {...componentStyles.gridContainer}>
                            <GridItem {...componentStyles.subjectGridItem}>
                                <Typography {...componentStyles.dateTimeText}>{timeDisplay}</Typography>
                                <Typography {...componentStyles.subjectText}>{message.subject}</Typography>
                            </GridItem>

                            <GridItem
                                {...componentStyles.actionButtonGridItem}
                                onClick={(event: any) => event.stopPropagation()}
                            >
                                <Hidden below="sm">
                                    <Link {...componentStyles.actionButton} onClick={handleClickMessageReadStatus}>
                                        {message.isRead ? translate("Mark as Unread") : translate("Mark as Read")}
                                    </Link>
                                </Hidden>
                                <Hidden above="xs">
                                    <OverflowMenu {...componentStyles.overflowMenu}>
                                        <Clickable onClick={handleClickMessageReadStatus}>
                                            {message.isRead ? translate("Mark as Unread") : translate("Mark as Read")}
                                        </Clickable>
                                    </OverflowMenu>
                                </Hidden>
                            </GridItem>
                        </GridContainer>
                    }
                    initialExpanded={false}
                    {...styles.messageCard?.accordionSection}
                >
                    <GridContainer {...componentStyles.gridContainer}>
                        <GridItem {...componentStyles.bodyGridItem}>
                            <Typography {...componentStyles.bodyText}>{message.body}</Typography>
                        </GridItem>
                        <GridItem {...componentStyles.bottomBorderGridItem}>
                            <StyledWrapper {...styles.messageCard?.bottomBorder} />
                        </GridItem>
                    </GridContainer>
                </ManagedAccordionSection>
            </Accordion>
        </StyledArticle>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountMessages);
